import { ArrowRight, PlayCircle, ShieldCheck, Sparkles } from "lucide-react";
import { BRAND, TRUST_STRIP } from "../data/mock";
import * as Icons from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Soft background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-50/60 via-white to-white" />
        <div className="absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-teal-200/30 blur-3xl" />
        <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-orange-200/30 blur-3xl" />
        <div className="absolute inset-0 dot-pattern opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="text-center lg:text-left">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-200 px-4 py-1.5 shadow-sm animate-fade-up"
              data-testid="hero-pill"
            >
              <Sparkles className="h-4 w-4 text-orange-500" />
              <span className="text-xs uppercase tracking-[0.18em] font-semibold text-teal-700">
                India's Smart Digital Hisab Book
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-heading mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-slate-900 animate-fade-up delay-100"
            >
              अब हिसाब रहेगा{" "}
              <span className="relative inline-block">
                <span className="relative z-10 text-orange-500">हमेशा डिजिटल</span>
                <span className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-orange-200/70 -z-0 rounded" />
              </span>
              .
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0 animate-fade-up delay-200"
            >
              Track credit accounts, EMI records & inventory with one powerful app —
              built for Indian shopkeepers, traders and growing businesses. No diary.
              No notebook. Just smart hisab.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 animate-fade-up delay-300">
              <a
                href="#pricing"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-2 rounded-full bg-orange-500 hover:bg-orange-600 px-7 py-3.5 text-sm md:text-base font-semibold text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 transition-all active:scale-95 animate-pulse-glow"
              >
                Start for Free
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#video"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 hover:border-teal-300 hover:bg-teal-50 px-6 py-3.5 text-sm md:text-base font-semibold text-slate-800 transition-all active:scale-95"
              >
                <PlayCircle className="h-5 w-5 text-teal-600" />
                Watch Demo
              </a>
            </div>

            <div
              className="mt-8 flex items-center justify-center lg:justify-start gap-2 text-sm text-slate-500 animate-fade-up delay-400"
              data-testid="hero-trust-line"
            >
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              Trusted by 10,000+ small businesses across India
            </div>
          </div>

          {/* Right: visual */}
          <div className="relative animate-fade-up delay-300">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-teal-200/40 via-white to-orange-200/40 blur-2xl -z-10" />
            <div className="rounded-[2rem] bg-white border border-slate-200 shadow-2xl shadow-slate-300/40 p-3 md:p-4 overflow-hidden">
              <img
                src={BRAND.heroImageUrl}
                alt="Reptile Hisabbook dashboard preview"
                className="w-full h-auto rounded-2xl"
                data-testid="hero-mockup-image"
                loading="eager"
              />
            </div>

            {/* Floating chip 1 */}
            <div className="hidden md:flex absolute -left-6 top-10 items-center gap-3 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 pr-4 animate-float">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Icons.MessageCircle className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-xs text-slate-500">WhatsApp invoice</div>
                <div className="text-sm font-semibold text-slate-900">Sent in 1 click</div>
              </div>
            </div>

            {/* Floating chip 2 */}
            <div className="hidden md:flex absolute -right-4 bottom-12 items-center gap-3 bg-white rounded-2xl shadow-xl border border-slate-100 p-3 pr-4 animate-float" style={{ animationDelay: "1.5s" }}>
              <div className="h-10 w-10 rounded-xl bg-orange-100 text-orange-700 flex items-center justify-center">
                <Icons.IndianRupee className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-xs text-slate-500">Today's collection</div>
                <div className="text-sm font-semibold text-slate-900">₹ 42,800</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div
          data-testid="trust-strip"
          className="mt-16 md:mt-20 rounded-3xl bg-gradient-to-r from-teal-600 to-teal-700 text-white py-6 md:py-8 px-6 md:px-10 shadow-xl shadow-teal-600/20"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {TRUST_STRIP.map((item, idx) => {
              const Icon = Icons[item.icon] || Icons.Check;
              return (
                <div
                  key={item.label}
                  data-testid={`trust-item-${idx}`}
                  className="flex items-center gap-3"
                >
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-white/15 flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="leading-tight">
                    <div className="font-heading text-base md:text-lg font-bold">
                      {item.label}
                    </div>
                    <div className="text-xs md:text-sm text-teal-50/90">
                      {item.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
